import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Loader2, LogIn, Mail, ClipboardList, Users, Timer, AlertCircle, Handshake, Settings2, MessageCircle, BarChart3, DatabaseBackup, GraduationCap } from "lucide-react";
import { Traffic } from "../components/admin/Traffic";
import { Backup } from "../components/admin/Backup";
import { AdminAcademy } from "../components/admin/AdminAcademy";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, fmtEur } from "../lib/api";
import { validate } from "../lib/validate";
import { Field, inputProps, LogoMark } from "../components/site/Primitives";
import { RequestsTable, ExpertsTable, ExpertFilter, InterventionsAdminTable, WhatsAppTable } from "../components/admin/AdminTables";
import { AdminPartners } from "../components/admin/AdminPartners";
import { AdminSettings } from "../components/admin/AdminSettings";
import { DeleteExamplesButton } from "../components/admin/Cleanup";

const Login = () => {
  const { t, lang, toggle } = useLang();
  const { signIn } = useAuth();
  const a = t.admin;
  const [values, setValues] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ busy: false, error: null });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values, { email: ["required", "email"], password: ["required"] });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setState({ busy: true, error: null });
    try {
      const { data } = await api.post("/auth/login", values);
      if (data.user.role !== "admin") return setState({ busy: false, error: a.invalid });
      signIn(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setState({ busy: false, error: err?.response?.status === 429 && typeof d === "string" ? d : a.invalid });
    }
  };

  return (
    <main className="login-wrap" data-testid="admin-login-page">
      <div className="panel login-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="logo"><LogoMark /> IndusHub</span>
          <button type="button" className="btn btn-ghost lang-btn btn-sm" onClick={toggle} data-testid="admin-lang-toggle-btn">{lang === "fr" ? "EN" : "FR"}</button>
        </div>
        <h1>{a.loginTitle}</h1>
        <p className="f-note" style={{ marginBottom: 24 }}>{a.loginSub}</p>
        <form onSubmit={submit} noValidate style={{ display: "grid", gap: 18 }} data-testid="admin-login-form">
          <Field id="admin-email" label={a.email} error={errors.email} required>
            <input type="email" autoComplete="username" {...inputProps("admin-email", errors.email, true)} value={values.email} onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))} />
          </Field>
          <Field id="admin-password" label={a.password} error={errors.password} required>
            <input type="password" autoComplete="current-password" {...inputProps("admin-password", errors.password, true)} value={values.password} onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))} />
          </Field>
          <Link to="/reset-password" className="link-btn forgot-link" data-testid="admin-forgot-link">{t.reset.forgot}</Link>
          {state.error && <p className="error-banner" role="alert" style={{ marginTop: 0 }} data-testid="admin-login-error"><AlertCircle aria-hidden="true" />{state.error}</p>}
          <button type="submit" className="btn btn-primary" disabled={state.busy} data-testid="admin-login-submit-btn">
            {state.busy ? <Loader2 className="spin" aria-hidden="true" /> : <LogIn aria-hidden="true" />}{a.submit}
          </button>
        </form>
        <Link to="/" className="icon-link" style={{ marginTop: 22 }} data-testid="admin-back-to-site-link"><ArrowLeft aria-hidden="true" />{a.back}</Link>
      </div>
    </main>
  );
};

const Kpis = ({ stats }) => {
  const { t, lang } = useLang();
  const s = t.admin.stats;
  const items = [
    [s.diagnostics, stats.diagnostics, `${stats.new_diagnostics} ${s.new.toLowerCase()}`],
    [s.experts, stats.experts, `${stats.example_experts} ${s.examples} · ${stats.pending_experts} ${t.approval.pendingLower}`],
    [s.interventions, stats.interventions],
    [s.revenue, fmtEur(stats.revenue_ht, lang)],
    [s.commission, fmtEur(stats.commission, lang)],
  ];
  return (
    <div className="kpis" data-testid="admin-kpis">
      {items.map(([l, v, sub], i) => <div className="panel kpi" key={l}><span>{l}</span><b data-testid={`admin-kpi-${i}`}>{v}</b>{sub && <small>{sub}</small>}</div>)}
    </div>
  );
};

const TABS = [["traffic", BarChart3], ["requests", ClipboardList], ["experts", Users], ["interventions", Timer], ["academy", GraduationCap], ["partners", Handshake], ["settings", Settings2], ["whatsapp", MessageCircle], ["backup", DatabaseBackup]];
const COUNTED = ["requests", "experts", "interventions", "whatsapp"];

const Dashboard = () => {
  const { t, lang, toggle } = useLang();
  const { user, signOut: onLogout } = useAuth();
  const a = t.admin;
  const [params] = useSearchParams();
  const [tab, setTab] = useState(TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "requests");
  const [expertFilter, setExpertFilter] = useState(["pending", "approved", "rejected"].includes(params.get("status")) ? params.get("status") : "all");
  const [data, setData] = useState({ stats: null, requests: [], experts: [], interventions: [], whatsapp: [] });

  const load = useCallback(async () => {
    try {
      const [stats, requests, experts, interventions, whatsapp] = await Promise.all(
        ["/admin/stats", "/admin/diagnostics", "/admin/experts", "/admin/interventions", "/admin/whatsapp-logs"].map((u) => api.get(u).then((r) => r.data))
      );
      setData({ stats, requests, experts, interventions, whatsapp });
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) onLogout();
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    try { await fn(); toast.success(a.updated); load(); } catch { toast.error(t.errors.generic); }
  };
  const onStatus = (id, status) => act(() => api.patch(`/admin/diagnostics/${id}`, { status }));
  const onToggle = (id, is_example) => act(() => api.patch(`/admin/experts/${id}`, { is_example }));
  const onAssign = (id, expert_id) => act(() => api.post(`/admin/diagnostics/${id}/assign`, { expert_id }));
  const onReassign = (id, expert_id) => act(() => api.patch(`/admin/interventions/${id}/assign`, { expert_id }));
  const onReview = (e, status) => {
    const reason = status === "rejected" ? window.prompt(t.approval.reasonPrompt(e.name), "") : null;
    if (status === "rejected" && reason === null) return;
    act(() => api.post(`/admin/experts/${e.id}/review`, { status, reason }));
  };
  const approved = data.experts.filter((e) => e.status === "approved");
  const pending = data.experts.length - approved.length - data.experts.filter((e) => e.status === "rejected").length;
  const shownExperts = expertFilter === "all" ? data.experts : data.experts.filter((e) => e.status === expertFilter);
  const onDelete = (e) => { if (window.confirm(a.confirmDel(e.name))) act(() => api.delete(`/admin/experts/${e.id}`)); };

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-top">
        <div className="wrap nav">
          <Link to="/" className="logo" data-testid="admin-logo-link"><LogoMark /> <span>IndusHub · Admin</span></Link>
          <div className="nav-right">
            <button type="button" className="btn btn-ghost lang-btn" onClick={toggle} data-testid="admin-dashboard-lang-toggle-btn">{lang === "fr" ? "EN" : "FR"}</button>
            <button type="button" className="btn btn-ghost" onClick={onLogout} data-testid="admin-logout-btn"><LogOut aria-hidden="true" />{a.logout}</button>
          </div>
        </div>
      </header>
      <main className="admin-main">
        <div className="wrap">
          <p className="eyebrow">{user?.email}</p>
          <h1 className="admin-title">{a.title}</h1>
          {data.stats && <Kpis stats={data.stats} />}
          {data.stats && (
            <p className="info-note" style={{ marginTop: 0, marginBottom: 28 }} data-testid="admin-email-status-note">
              <Mail aria-hidden="true" /><span>{data.stats.email_notifications ? a.emailOn : a.emailOff} · {data.stats.whatsapp_missing.length ? t.adminX.waOff(data.stats.whatsapp_missing.join(", ")) : t.adminX.waOn}</span>
            </p>
          )}
          <div className="tabs" role="tablist">
            {TABS.map(([k, Icon]) => (
              <button key={k} type="button" role="tab" className="tab-btn" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}>
                <Icon aria-hidden="true" />{a.tabs[k] || t.adminX.tabs[k] || { backup: t.backup.tab, academy: t.academy.admin.tab }[k] || t.traffic.tab}{COUNTED.includes(k) && ` (${data[k].length})`}
                {k === "experts" && pending > 0 && <span className="tab-badge" aria-label={t.approval.badge(pending)} title={t.approval.badge(pending)} data-testid="admin-experts-pending-badge">{pending}</span>}
              </button>
            ))}
          </div>
          <div className="panel pro-panel tab-panel" key={tab} role="tabpanel">
            {tab === "requests" && <RequestsTable rows={data.requests} experts={approved} onStatus={onStatus} onAssign={onAssign} />}
            {tab === "experts" && (
              <div className="expert-toolbar">
                <ExpertFilter rows={data.experts} value={expertFilter} onChange={setExpertFilter} />
                <DeleteExamplesButton count={data.experts.filter((e) => e.is_example).length} onDone={load} />
              </div>
            )}
            {tab === "experts" && <ExpertsTable rows={shownExperts} onToggle={onToggle} onDelete={onDelete} onReview={onReview} />}
            {tab === "interventions" && <InterventionsAdminTable rows={data.interventions} experts={approved} onReassign={onReassign} />}
            {tab === "partners" && <AdminPartners />}
            {tab === "traffic" && <Traffic />}
            {tab === "settings" && <AdminSettings />}
            {tab === "whatsapp" && <WhatsAppTable rows={data.whatsapp} />}
            {tab === "backup" && <Backup />}
            {tab === "academy" && <AdminAcademy />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default function AdminPage() {
  const { user } = useAuth();
  if (user === undefined) return <div className="login-wrap"><Loader2 className="spin" aria-label="Loading" /></div>;
  return user?.role === "admin" ? <Dashboard /> : <Login />;
}
